import { Button, FAIcon, TableMenu } from '@/components'
import type { TableMenuItem } from '@/components'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO } from 'date-fns'

interface FixedAddon {
  id: string
  validFrom?: string | null
  validTo?: string | null
  salaryCode?: { name: string } | null
}

const formatDate = (date: string | null | undefined) =>
  date ? format(parseISO(date), 'dd.MM.yyyy') : ''

export const FixedAddonList = <TAddon extends FixedAddon>({
  title,
  addons,
  loading,
  canAdd,
  canEdit,
  canDelete,
  onAdd,
  onEdit,
  onDelete,
}: {
  title: string
  addons: TAddon[]
  loading: boolean
  canAdd: boolean
  canEdit: boolean
  canDelete: boolean
  onAdd(): void
  onEdit(addon: TAddon): void
  onDelete(addon: TAddon): void
}) => {
  const translations = useTranslate({
    add: 'common.add',
    edit: 'common.edit',
    delete: 'common.delete',
    noResults: 'common.no-results',
  })

  const menuItems = (addon: TAddon): TableMenuItem[] => [
    ...(canEdit
      ? [
          {
            id: 'edit',
            type: 'regular' as const,
            label: translations.edit,
            icon: 'pen' as const,
            onClick: () => onEdit(addon),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            id: 'delete',
            type: 'regular' as const,
            label: translations.delete,
            icon: 'trash' as const,
            onClick: () => onDelete(addon),
          },
        ]
      : []),
  ]

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <header className="flex items-center justify-between gap-4 border-b border-stone-200 px-5 py-3 sm:px-6">
        <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
        {canAdd && (
          <Button type="button" variant="outline" size="lg" onClick={onAdd}>
            <FAIcon icon="plus" />
            {translations.add}
          </Button>
        )}
      </header>

      {loading ? (
        <div className="px-5 py-4 sm:px-6">
          <Loader.Spinner />
        </div>
      ) : addons.length === 0 ? (
        <p className="px-5 py-4 text-sm text-stone-500 sm:px-6">
          {translations.noResults}
        </p>
      ) : (
        <ul className="divide-y divide-stone-200">
          {addons.map(addon => {
            const items = menuItems(addon)
            const validFrom = formatDate(addon.validFrom)
            const validTo = formatDate(addon.validTo)
            return (
              <li
                key={addon.id}
                className="flex items-center justify-between gap-4 px-5 py-3 sm:px-6"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-900">
                    {addon.salaryCode?.name ?? '-'}
                  </p>
                  {(validFrom || validTo) && (
                    <p className="text-xs text-stone-500">
                      {validFrom} – {validTo}
                    </p>
                  )}
                </div>
                {items.length > 0 && <TableMenu items={items} />}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
